import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  Refresh as RefreshIcon,
  Sms as SmsIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  SAFETY_ALERT_DISPATCH_LOG_UPDATED_EVENT,
  SAFETY_ALERT_DISPATCH_LOG_SYNC_STORAGE_KEY,
  safetyAlertDispatchService,
  type SafetyAlertDispatchLogItem,
} from '../../services/safetyAlertDispatchService';
import { DISPATCH_HISTORY_TABLE_MAX_HEIGHT } from '../../constants/scrollPanel';
import {
  dispatchResultCellHint,
  formatDispatchDetailLines,
  formatDispatchSuccessLines,
} from '../../utils/safetyAlertLogDisplay';
import WidgetListPagination from '../common/WidgetListPagination';
import EventSnapshotThumb from '../events/EventSnapshotThumb';
import { API_ORIGIN } from '../../config';

function dispatchLogImageSrc(imageUrl?: string | null): string | null {
  if (!imageUrl?.trim()) return null;
  const path = imageUrl.trim();
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${API_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR');
  } catch {
    return iso;
  }
}

function channelChipColor(channel: string): 'default' | 'primary' | 'secondary' | 'info' | 'warning' {
  if (channel === 'sms') return 'primary';
  if (channel === 'kakao') return 'secondary';
  if (channel === 'wearable') return 'info';
  if (channel === 'none') return 'warning';
  return 'default';
}

export type SafetyAlertDispatchHistoryCardProps = {
  poolId?: number;
  poolName?: string;
  showPoolColumn?: boolean;
  title?: string;
  subheader?: string;
  maxHeight?: number | string;
  /** 설정 시 카드에 페이지당 건수만 표시하고 이전/다음·전체보기(팝업) 제공 */
  pageSize?: number;
  fullViewTitle?: string;
};

function DispatchResultCell({ row }: { row: SafetyAlertDispatchLogItem }) {
  if (row.success) {
    const lines = formatDispatchSuccessLines(row);
    return (
      <Box>
        <Chip size="small" label="성공" color="success" />
        {lines.map((line) => (
          <Typography
            key={line}
            variant="caption"
            display="block"
            color="text.secondary"
            sx={{ whiteSpace: 'normal', lineHeight: 1.35 }}
          >
            {line}
          </Typography>
        ))}
      </Box>
    );
  }
  const hint = dispatchResultCellHint(row);
  return (
    <Box>
      <Chip size="small" label="실패" color="error" />
      {hint ? (
        <Typography variant="caption" display="block" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

function DispatchDetailBody({ row }: { row: SafetyAlertDispatchLogItem }) {
  const lines = formatDispatchDetailLines(row);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2">
        <strong>일시</strong> {formatWhen(row.created_at)}
      </Typography>
      <Typography variant="body2">
        <strong>채널</strong> {row.channel_label}
      </Typography>
      <Typography variant="body2">
        <strong>결과</strong>{' '}
        {row.success ? (
          <Chip size="small" label="성공" color="success" sx={{ ml: 0.5 }} />
        ) : (
          <Chip size="small" label="실패" color="error" sx={{ ml: 0.5 }} />
        )}
      </Typography>
      <Typography variant="body2">
        <strong>메시지</strong>
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {row.message}
      </Typography>
      {dispatchLogImageSrc(row.image_url) ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>첨부 이미지</strong>
          </Typography>
          <EventSnapshotThumb
            src={dispatchLogImageSrc(row.image_url)}
            large
            alt="알림 첨부 이미지"
          />
        </Box>
      ) : null}
      <Typography variant="body2" sx={{ mt: 1 }}>
        <strong>{row.success ? '발송 상세' : '실패 상세'}</strong>
      </Typography>
      {lines.map((line) => (
        <Typography key={line} variant="body2" color="text.secondary">
          {line}
        </Typography>
      ))}
    </Box>
  );
}

const SafetyAlertDispatchHistoryCard: React.FC<SafetyAlertDispatchHistoryCardProps> = ({
  poolId,
  poolName,
  showPoolColumn = poolId == null,
  title = '현장 안전 알림 발송 이력',
  subheader = 'SMS · 카카오 알림톡 · 웨어러블 전송 결과 (최근순)',
  maxHeight = DISPATCH_HISTORY_TABLE_MAX_HEIGHT,
  pageSize,
  fullViewTitle = '현장 안전 알림 발송 이력 전체',
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<SafetyAlertDispatchLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [detailRow, setDetailRow] = useState<SafetyAlertDispatchLogItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [listPage, setListPage] = useState(0);
  const [fullListOpen, setFullListOpen] = useState(false);

  const paginated = pageSize != null && pageSize > 0;
  const pageCount = paginated ? Math.max(1, Math.ceil(items.length / pageSize)) : 1;
  const safePage = paginated ? Math.min(listPage, pageCount - 1) : 0;
  const visibleItems = paginated
    ? items.slice(safePage * pageSize, safePage * pageSize + pageSize)
    : items;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await safetyAlertDispatchService.list({
        poolId,
        limit: 200,
        offset: 0,
      });
      const next = Array.isArray(res?.items) ? res.items : [];
      setItems(next);
      setTotal(Number(res?.total) || next.length);
      setSelected(new Set());
    } catch (e) {
      // 이력 없음·권한·일시 오류 모두 빈 목록으로 — 시설 진입 시 붉은 오류 문구 내지 않음
      console.warn('safety alert dispatch history load skipped', e);
      setItems([]);
      setTotal(0);
      setSelected(new Set());
    } finally {
      setLoading(false);
    }
  }, [poolId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onUpdate = () => {
      void load();
    };
    window.addEventListener(SAFETY_ALERT_DISPATCH_LOG_UPDATED_EVENT, onUpdate);
    const onStorage = (e: StorageEvent) => {
      if (e.key === SAFETY_ALERT_DISPATCH_LOG_SYNC_STORAGE_KEY) void load();
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SAFETY_ALERT_DISPATCH_LOG_UPDATED_EVENT, onUpdate);
      window.removeEventListener('storage', onStorage);
    };
  }, [load]);

  useEffect(() => {
    setListPage(0);
  }, [items.length, poolId, pageSize]);

  const allSelected = visibleItems.length > 0 && selected.size === visibleItems.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visibleItems.map((r) => r.id)));
  };

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDeleteOne = async (id: number) => {
    if (!window.confirm('이 발송 이력 삭제 확인')) return;
    setBusy(true);
    try {
      await safetyAlertDispatchService.deleteOne(id, poolId);
      enqueueSnackbar('삭제함', { variant: 'success' });
      await load();
    } catch (e) {
      console.error(e);
      enqueueSnackbar('삭제에 실패', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleBulkDeleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`선택한 ${selected.size}건 삭제 확인`)) return;
    setBusy(true);
    try {
      const n = await safetyAlertDispatchService.bulkDelete({
        ids: [...selected],
        poolId,
      });
      enqueueSnackbar(`${n}건 삭제함`, { variant: 'success' });
      await load();
    } catch (e) {
      console.error(e);
      enqueueSnackbar('일괄 삭제에 실패', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteAll = async () => {
    const scope =
      poolId != null
        ? poolName
          ? `「${poolName}」 시설의 `
          : '이 시설의 '
        : '전체 ';
    if (!window.confirm(`${scope}발송 이력 전체 삭제 확인`)) return;
    setBusy(true);
    try {
      const n = await safetyAlertDispatchService.bulkDelete({ all: true, poolId });
      enqueueSnackbar(`${n}건 삭제함`, { variant: 'success' });
      await load();
    } catch (e) {
      console.error(e);
      enqueueSnackbar('전체 삭제에 실패', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const headerSub = useMemo(() => {
    const base = subheader;
    if (paginated && items.length > 0) {
      return `${base} · 전체 ${items.length}건`;
    }
    if (total > items.length) return `${base} · 표시 ${items.length} / 전체 ${total}`;
    if (total > 0) return `${base} · ${total}건`;
    return base;
  }, [subheader, total, items.length, paginated]);

  const renderDispatchTable = (
    rows: SafetyAlertDispatchLogItem[],
    stickyHeader = true,
  ) => (
    <Table size="small" stickyHeader={stickyHeader}>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={someSelected}
              checked={allSelected}
              onChange={toggleAll}
              disabled={busy || rows.length === 0}
            />
          </TableCell>
          <TableCell>일시</TableCell>
          {showPoolColumn && <TableCell>시설</TableCell>}
          <TableCell>알림 방법</TableCell>
          <TableCell>내용</TableCell>
          <TableCell>결과</TableCell>
          <TableCell align="right">삭제</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow
            key={row.id}
            hover
            selected={selected.has(row.id)}
            sx={{ cursor: 'pointer' }}
            onClick={() => setDetailRow(row)}
          >
            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
              <Checkbox
                checked={selected.has(row.id)}
                onChange={() => toggleOne(row.id)}
                disabled={busy}
              />
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatWhen(row.created_at)}</TableCell>
            {showPoolColumn && (
              <TableCell>{row.pool_name || `#${row.pool_id}`}</TableCell>
            )}
            <TableCell>
              <Chip
                size="small"
                label={row.channel_label}
                color={channelChipColor(row.channel)}
                variant="outlined"
              />
            </TableCell>
            <TableCell
              sx={{
                maxWidth: 320,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {dispatchLogImageSrc(row.image_url) ? (
                  <EventSnapshotThumb
                    src={dispatchLogImageSrc(row.image_url)}
                    width={48}
                    height={36}
                    alt="첨부"
                  />
                ) : null}
                <Typography
                  variant="body2"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {row.message}
                </Typography>
              </Box>
            </TableCell>
            <TableCell>
              <DispatchResultCell row={row} />
            </TableCell>
            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
              <IconButton
                size="small"
                color="error"
                disabled={busy}
                onClick={() => void handleDeleteOne(row.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title={title}
          subheader={headerSub}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <Tooltip title="새로고침">
                <span>
                  <IconButton onClick={() => void load()} disabled={loading || busy} size="small">
                    {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteSweepIcon />}
                disabled={busy || selected.size === 0}
                onClick={() => void handleBulkDeleteSelected()}
              >
                선택 삭제
              </Button>
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={busy || total === 0}
                onClick={() => void handleDeleteAll()}
              >
                전체 삭제
              </Button>
            </Box>
          }
        />
        <CardContent sx={{ pt: 0 }}>
          {loading && items.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : items.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              저장된 알림 발송 이력 없음
            </Typography>
          ) : (
            <>
              <TableContainer
                sx={{
                  maxHeight: paginated ? undefined : maxHeight,
                  overflow: paginated ? 'visible' : 'auto',
                }}
              >
                {renderDispatchTable(visibleItems, !paginated)}
              </TableContainer>
              {paginated ? (
                <WidgetListPagination
                  page={safePage}
                  pageCount={pageCount}
                  totalItems={items.length}
                  pageSize={pageSize!}
                  onPageChange={setListPage}
                  onOpenFullView={() => setFullListOpen(true)}
                  disabled={loading || busy}
                />
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={fullListOpen}
        onClose={() => setFullListOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>{fullViewTitle}</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto' }}>
            {renderDispatchTable(items)}
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFullListOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailRow != null} onClose={() => setDetailRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {detailRow?.success ? '알림 발송 상세' : '알림 발송 실패 상세'}
        </DialogTitle>
        <DialogContent dividers>
          {detailRow ? (
            <>
              {showPoolColumn && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>시설</strong> {detailRow.pool_name || `#${detailRow.pool_id}`}
                </Typography>
              )}
              <DispatchDetailBody row={detailRow} />
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailRow(null)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SafetyAlertDispatchHistoryCard;

export type SafetyAlertByEventDialogProps = {
  open: boolean;
  onClose: () => void;
  eventId: number | null;
  eventTitle?: string;
};

export const SafetyAlertByEventDialog: React.FC<SafetyAlertByEventDialogProps> = ({
  open,
  onClose,
  eventId,
  eventTitle,
}) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<SafetyAlertDispatchLogItem[]>([]);
  const [failureRow, setFailureRow] = useState<SafetyAlertDispatchLogItem | null>(null);

  useEffect(() => {
    if (!open || eventId == null) {
      setItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await safetyAlertDispatchService.list({ eventId, limit: 50 });
        if (!cancelled) setItems(res.items);
      } catch (e) {
        console.error(e);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, eventId]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmsIcon color="primary" />
          이벤트 알림 발송 내역
          {eventTitle ? ` — ${eventTitle}` : ''}
        </DialogTitle>
        <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary">이 이벤트에 연결된 알림 발송 기록 없음.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>일시</TableCell>
                <TableCell>시설</TableCell>
                <TableCell>방법</TableCell>
                <TableCell>내용</TableCell>
                <TableCell>결과</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setFailureRow(row)}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatWhen(row.created_at)}</TableCell>
                  <TableCell>{row.pool_name || `#${row.pool_id}`}</TableCell>
                  <TableCell>{row.channel_label}</TableCell>
                  <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {row.message}
                  </TableCell>
                  <TableCell>
                    <DispatchResultCell row={row} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>닫기</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={failureRow != null}
        onClose={() => setFailureRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {failureRow?.success ? '알림 발송 상세' : '알림 발송 실패 상세'}
        </DialogTitle>
        <DialogContent dividers>
          {failureRow ? <DispatchDetailBody row={failureRow} /> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFailureRow(null)}>닫기</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
